/**
 * Utilidades para generar paletas de colores armoniosas
 * y validar contrastes de accesibilidad
 */

/**
 * Convierte hex a RGB
 */
export function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
}

/**
 * Convierte RGB a hex
 */
export function rgbToHex(r, g, b) {
  return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
}

/**
 * Convierte hex a HSL
 */
export function hexToHsl(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  
  const r = rgb.r / 255;
  const g = rgb.g / 255;
  const b = rgb.b / 255;
  
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;
  
  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: h = 0;
    }
  }
  
  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100)
  };
}

/**
 * Convierte HSL a hex
 */
export function hslToHex(h, s, l) {
  h = h / 360;
  s = s / 100;
  l = l / 100;
  
  let r, g, b;
  
  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };
    
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    
    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);
  }
  
  return rgbToHex(
    Math.round(r * 255),
    Math.round(g * 255),
    Math.round(b * 255)
  );
}

/**
 * Calcula el contraste entre dos colores (ratio WCAG)
 */
export function getContrastRatio(hex1, hex2) {
  const rgb1 = hexToRgb(hex1);
  const rgb2 = hexToRgb(hex2);
  
  if (!rgb1 || !rgb2) return 1;
  
  const getLuminance = (r, g, b) => {
    const [rs, gs, bs] = [r, g, b].map(c => {
      c = c / 255;
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
  };
  
  const l1 = getLuminance(rgb1.r, rgb1.g, rgb1.b);
  const l2 = getLuminance(rgb2.r, rgb2.g, rgb2.b);
  
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Genera un color de texto legible sobre un fondo
 */
export function getReadableTextColor(backgroundColor) {
  const hsl = hexToHsl(backgroundColor);
  if (!hsl) return '#000000';
  
  // Si el fondo es claro, usa texto oscuro
  // Si el fondo es oscuro, usa texto claro
  return hsl.l > 50 ? '#1a202c' : '#ffffff';
}

/**
 * Genera una paleta de colores armoniosa basada en un color principal
 */
export function generateColorPalette(primaryColor) {
  const hsl = hexToHsl(primaryColor);
  if (!hsl) return null;
  
  const { h, s, l } = hsl;
  
  // Color principal
  const primary = primaryColor;
  
  // Color secundario (análogo, +30 grados en matiz)
  const secondary = hslToHex((h + 30) % 360, s, l);
  
  // Color de acento (complementario, +180 grados)
  const accent = hslToHex((h + 180) % 360, s, l);
  
  // Color hover (mismo matiz, más saturación y un poco más claro)
  const hover = hslToHex(h, Math.min(s + 10, 100), Math.min(l + 10, 90));
  
  // Color de fondo claro
  const backgroundLight = hslToHex(h, Math.max(s - 40, 10), 95);
  
  // Color de fondo oscuro
  const backgroundDark = hslToHex(h, Math.max(s - 20, 20), 15);
  
  // Color de borde
  const border = hslToHex(h, Math.max(s - 30, 15), 80);
  
  // Texto sobre fondo claro
  const textOnLight = '#1a202c';
  
  // Texto sobre fondo oscuro
  const textOnDark = '#ffffff';
  
  // Texto sobre color principal
  const textOnPrimary = getReadableTextColor(primary);
  
  return {
    primary,
    secondary,
    accent,
    hover,
    backgroundLight,
    backgroundDark,
    border,
    textOnLight,
    textOnDark,
    textOnPrimary,
    // Verifica contrastes
    contrasts: {
      primaryVsLight: getContrastRatio(primary, backgroundLight),
      primaryVsDark: getContrastRatio(primary, backgroundDark),
      textOnPrimaryContrast: getContrastRatio(textOnPrimary, primary)
    }
  };
}

/**
 * Valida si un color tiene buen contraste con otro
 */
export function hasGoodContrast(color1, color2, level = 'AA') {
  const ratio = getContrastRatio(color1, color2);
  
  // WCAG 2.1 standards
  if (level === 'AAA') {
    return ratio >= 7; // AAA para texto normal
  }
  return ratio >= 4.5; // AA para texto normal
}

/**
 * Ajusta un color para tener buen contraste con un fondo
 */
export function adjustColorForContrast(color, background, targetRatio = 4.5) {
  let hsl = hexToHsl(color);
  if (!hsl) return color;
  
  const bgHsl = hexToHsl(background);
  if (!bgHsl) return color;
  
  // Si el fondo es claro, oscurece el color
  // Si el fondo es oscuro, aclara el color
  const step = bgHsl.l > 50 ? -5 : 5;
  let attempts = 0;
  const maxAttempts = 20;
  
  while (getContrastRatio(hslToHex(hsl.h, hsl.s, hsl.l), background) < targetRatio && attempts < maxAttempts) {
    hsl.l = Math.max(0, Math.min(100, hsl.l + step));
    attempts++;
  }
  
  return hslToHex(hsl.h, hsl.s, hsl.l);
}
